import { useEffect, useState } from "react";

import {
  Box,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import ApartmentIcon from "@mui/icons-material/Apartment";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import LocalCarWashOutlinedIcon from "@mui/icons-material/LocalCarWashOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";

import {
  useFetchOrganizationsQuery,
  useFetchOrganizationFeaturesQuery,
} from "../../api";

import { useFetchSuperAdminDashboardQuery } from "../../api/reportService";

import { Features as FeaturesEnum } from "../../enums";
import { OrganizationModel } from "../../models";

import { DropdownItem } from "../../types";

import {
  formatCurrency,
  getDateRange,
  getTotalNumberOfUsers,
} from "../../utils";

import {
  DashboardTile,
  InvoiceStatusDonut,
  RevenueTrendChart,
  TopPerformingTenantsTable,
  TopWashTypesChart,
} from "./fragments";
import {
  DashboardPeriod,
  getSuperAdminDashboardMetrics,
} from "../../utils/dashboardUtil";
import {
  buildRevenueTrendData,
  getSelectedOrgAccountCount,
} from "../../utils/dashboardUtil";

const CURRENT_RANGE_KEY: Record<DashboardPeriod, string> = {
  daily: "today",
  weekly: "thisWeek",
  monthly: "thisMonth",
};

const PREVIOUS_RANGE_KEY: Record<DashboardPeriod, string> = {
  daily: "yesterday",
  weekly: "lastWeek",
  monthly: "lastMonth",
};

export const SuperAdminDash = () => {
  const [selectedOrgId, setSelectedOrgId] = useState(0);
  const [period, setPeriod] = useState<DashboardPeriod>("monthly");

  const [organizationsDropdown, setOrganizationsDropdown] = useState<
    DropdownItem[]
  >([{ key: "All Organizations", value: 0 }]);

  const currentRange = getDateRange(CURRENT_RANGE_KEY[period]);
  const previousRange = getDateRange(PREVIOUS_RANGE_KEY[period]);
  const chartRange = getDateRange("thisMonth");
  const fetchDashboardQuery = useFetchSuperAdminDashboardQuery(
    selectedOrgId,
    currentRange.startDate,
    currentRange.endDate,
  );
  const fetchPreviousDashboardQuery = useFetchSuperAdminDashboardQuery(
    selectedOrgId,
    previousRange.startDate,
    previousRange.endDate,
  );
  const fetchChartDashboardQuery = useFetchSuperAdminDashboardQuery(
    selectedOrgId,
    chartRange.startDate,
    chartRange.endDate,
  );

  const fetchOrganizationsQuery = useFetchOrganizationsQuery();

  const fetchOrganizationFeaturesQuery = useFetchOrganizationFeaturesQuery(
    selectedOrgId,
    { enabled: selectedOrgId > 0 },
  );

  const dashboardItems = fetchDashboardQuery.data;
  const previousDashboardItems = fetchPreviousDashboardQuery.data;

  useEffect(() => {
    if (fetchOrganizationsQuery.isSuccess) {
      setOrganizationsDropdown([
        { key: "All Organizations", value: 0 },
        ...OrganizationModel.buildOrganizationDropdown(
          fetchOrganizationsQuery.data,
        ),
      ]);
    }
  }, [fetchOrganizationsQuery.isSuccess, fetchOrganizationsQuery.data]);

  const selectedOrgName = organizationsDropdown.find(
    (organization) => organization.value === selectedOrgId,
  )?.key;

  const revenueTrendData = buildRevenueTrendData(fetchChartDashboardQuery.data);

  const {
    avgInvoiceAmount,
    avgVehiclesPerDay,
    currentInvoiceCount,
    currentRevenue,
    currentWashes,
    previousRevenue,
    previousWashes,
    previousAvgWashRate,
    previousPeriodLabel,
    revenueGrowth,
    washesGrowth,
  } = getSuperAdminDashboardMetrics({
    dashboardItems,
    previousDashboardItems,
    period,
  });

  const hasDashboardFeature =
    selectedOrgId === 0 ||
    (fetchOrganizationFeaturesQuery.data?.find(
      (item) => item.feature === FeaturesEnum.DASHBOARD,
    )?.enabled ??
      false);

  const allowDailyFilter = hasDashboardFeature;
  const showInvoiceDonut = hasDashboardFeature;
  const showTopPerformingAccounts = hasDashboardFeature;

  const selectedOrgAccountCount = getSelectedOrgAccountCount(
    dashboardItems,
    selectedOrgId,
    selectedOrgName,
  );

  useEffect(() => {
    if (period === "daily" && !allowDailyFilter) {
      setPeriod("weekly");
    }
  }, [period, allowDailyFilter]);

  return (
    <Box
      sx={{
        height: "fit-content",
        minHeight: "90vh",
        display: "flex",
        width: "100%",
        flexGrow: 1,
        flexDirection: "column",
        backgroundColor: "#F8FAFC",
        borderRadius: { xs: 0, sm: "20px" },
        px: { xs: 1.5, sm: 2 },
        py: { xs: 2, sm: 3 },
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 3 },
          width: "100%",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 24, sm: 32 },
              fontWeight: 700,
              color: "#0F172A",
              mb: 0.5,
              lineHeight: 1.15,
            }}
          >
            {selectedOrgId === 0 ? "Sprayzapp" : selectedOrgName}, Dashboard
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#94A3B8" }}>
            Fleet operations and job history overview
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexShrink: 0,
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: "100%", sm: 220 },
              maxWidth: { xs: "100%", sm: 320 },
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
            }}
          >
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              label="Organization"
              onChange={(event) => setSelectedOrgId(Number(event.target.value))}
              sx={{ height: 42, borderRadius: "12px" }}
            >
              {organizationsDropdown.map((organization) => (
                <MenuItem key={organization.value} value={organization.value}>
                  {organization.key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              backgroundColor: "#F1F5F9",
              borderRadius: "12px",
              p: 0.5,
              gap: 0.5,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {(allowDailyFilter
              ? (["daily", "weekly", "monthly"] as const)
              : (["weekly", "monthly"] as const)
            ).map((item) => (
              <Box
                key={item}
                onClick={() => setPeriod(item)}
                sx={{
                  px: 2,
                  py: 1,
                  flex: { xs: 1, sm: "0 0 auto" },
                  textAlign: "center",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 14 },
                  textTransform: "capitalize",
                  backgroundColor: period === item ? "#FFFFFF" : "transparent",
                  boxShadow:
                    period === item ? "0px 1px 4px rgba(0,0,0,.08)" : "none",
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
          {selectedOrgId === 0 ? (
            <DashboardTile
              title="Organizations"
              value={dashboardItems?.numberOfOrgs ?? 0}
              subtitle="Total active organizations"
              icon={<ApartmentIcon />}
              isLoading={fetchDashboardQuery.isLoading}
            />
          ) : (
            <DashboardTile
              title="Active Accounts"
              value={selectedOrgAccountCount}
              subtitle="Total active accounts"
              icon={<PeopleOutlineIcon />}
              isLoading={fetchDashboardQuery.isLoading}
            />
          )}
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
          <DashboardTile
            title="Users"
            value={getTotalNumberOfUsers(
              dashboardItems?.usersGroupedByRole ?? [],
            )}
            subtitle="Total registered users on platform"
            icon={<PeopleOutlineIcon />}
            isLoading={fetchDashboardQuery.isLoading}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
          <DashboardTile
            title="Washes"
            value={currentWashes}
            growth={washesGrowth}
            subtitle={
              period === "daily"
                ? "Washes completed today"
                : `${previousPeriodLabel}: ${previousWashes} | Avg Wash Rate: ${formatCurrency(previousAvgWashRate)}`
            }
            icon={<LocalCarWashOutlinedIcon />}
            isLoading={fetchDashboardQuery.isLoading}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6, lg: 3 }}>
          <DashboardTile
            title="Revenue"
            value={formatCurrency(currentRevenue)}
            growth={revenueGrowth}
            subtitle={
              period === "daily"
                ? "Revenue today"
                : `${previousPeriodLabel}: ${formatCurrency(previousRevenue)}`
            }
            valueColor="#3E8BD2"
            icon={<AttachMoneyIcon />}
            isLoading={fetchDashboardQuery.isLoading}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, lg: showInvoiceDonut ? 8 : 12 }}>
          <RevenueTrendChart data={revenueTrendData} />
        </Grid2>

        {showInvoiceDonut && (
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <InvoiceStatusDonut
              paymentReceived={
                dashboardItems?.invoicesGroupedByStatus.find(
                  (item) => item.key === "payment-received",
                )?.invoiceAmount ?? 0
              }
              sendToCustomer={
                dashboardItems?.invoicesGroupedByStatus.find(
                  (item) => item.key === "send-to-customer",
                )?.invoiceAmount ?? 0
              }
              inProgress={
                dashboardItems?.invoicesGroupedByStatus.find(
                  (item) => item.key === "in-progress",
                )?.invoiceAmount ?? 0
              }
              pending={
                dashboardItems?.invoicesGroupedByStatus.find(
                  (item) => item.key === "pending",
                )?.invoiceAmount ?? 0
              }
              pastDueInvoicesCount={dashboardItems?.pastDueInvoicesCount ?? 0}
              pastDueInvoicesAmount={dashboardItems?.pastDueInvoicesAmount ?? 0}
            />
          </Grid2>
        )}
      </Grid2>

      <Grid2 container spacing={2} sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, lg: 8 }}>
          <TopWashTypesChart
            data={dashboardItems?.washesGroupedByWashType ?? []}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, lg: 4 }}>
          <Box
            sx={{
              minHeight: { xs: 0, sm: 420 },
              height: { xs: "auto", sm: 420 },
              px: 2,
              py: 2,
              borderRadius: "18px",
              backgroundColor: "#fff",
              boxShadow: "0px 2px 12px rgba(0,0,0,.08)",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2 }}>
              Company Performance Details
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  p: 3,
                  pr: { xs: 9, sm: 8 },
                  mb: 2,
                  borderRadius: "18px",
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                }}
              >
                <AttachMoneyIcon
                  sx={{
                    position: "absolute",
                    right: 24,
                    top: 20,
                    fontSize: { xs: 38, sm: 50 },
                    color: "#10B981",
                    backgroundColor: "rgba(16,185,129,.12)",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Average Amount per Invoice
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: { xs: 30, sm: 42 },
                    fontWeight: 700,
                    color: "#10B981",
                    lineHeight: 1,
                  }}
                >
                  {formatCurrency(avgInvoiceAmount)}
                </Typography>
                <Typography sx={{ mt: 1, color: "#94A3B8" }}>
                  Current billing cycle invoice count: {currentInvoiceCount}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  p: 3,
                  pr: { xs: 9, sm: 8 },
                  borderRadius: "18px",
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                }}
              >
                <DirectionsCarIcon
                  sx={{
                    position: "absolute",
                    right: 24,
                    top: 20,
                    fontSize: { xs: 38, sm: 50 },
                    color: "#2563EB",
                    backgroundColor: "rgba(37,99,235,.12)",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#94A3B8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  {period === "daily"
                    ? "Vehicles Today"
                    : period === "weekly"
                      ? "Avg Vehicles / Week"
                      : "Avg Vehicles / Month"}
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: { xs: 30, sm: 42 },
                    fontWeight: 700,
                    color: "#2563EB",
                    lineHeight: 1,
                  }}
                >
                  {avgVehiclesPerDay.toFixed(1)}
                </Typography>
                <Typography sx={{ mt: 1, color: "#94A3B8" }}>
                  {period === "daily"
                    ? `${currentWashes} washes today`
                    : period === "weekly"
                      ? `${currentWashes} washes this week`
                      : `${currentWashes} washes this month`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid2>
      </Grid2>

      {showTopPerformingAccounts && (
        <Grid2 container>
          <Grid2 size={12}>
            <TopPerformingTenantsTable
              washes={dashboardItems?.washesGroupedByAccount ?? []}
              invoices={dashboardItems?.invoicesGroupedByAccount ?? []}
            />
          </Grid2>
        </Grid2>
      )}
    </Box>
  );
};
